/**
 * Reading the "a beta claim is already held" hint on the client.
 *
 * The real claim cookie (`bb_beta_claim`) is httpOnly and stays that way, so
 * the browser cannot see it. `POST /beta/claim` therefore sets a second,
 * readable cookie carrying only the claim KIND — see BETA_CLAIM_MARKER_COOKIE
 * in apps/api/src/services/beta-gate.ts, which owns both and clears both.
 *
 * This is a UI hint, never authorization: the server re-validates the real
 * cookie against the database on every registration. So this parser fails
 * closed on anything it does not recognize — an unreadable marker costs a
 * disabled button, and a forged one buys nothing.
 */

import type { BetaClaimKind } from "./api/beta";

/** Must match BETA_CLAIM_MARKER_COOKIE in apps/api/src/services/beta-gate.ts. */
export const BETA_CLAIM_MARKER_COOKIE = "bb_beta_claim_kind";

const CLAIM_KINDS: readonly string[] = ["code", "household"];

/** The kind of claim the visitor already holds, or null. */
export function readHeldBetaClaimKind(): BetaClaimKind | null {
  // Called from an effect, but guard anyway: this module must stay importable
  // from server-rendered code.
  if (typeof document === "undefined") {
    return null;
  }

  for (const entry of document.cookie.split(";")) {
    const separator = entry.indexOf("=");
    if (separator < 0) {
      continue;
    }
    if (entry.slice(0, separator).trim() !== BETA_CLAIM_MARKER_COOKIE) {
      continue;
    }
    let value: string;
    try {
      value = decodeURIComponent(entry.slice(separator + 1).trim());
    } catch {
      return null;
    }
    return CLAIM_KINDS.includes(value) ? (value as BetaClaimKind) : null;
  }

  return null;
}
